import {
  AccountClient,
  HasPersonalInformation,
  PersonalInformationRequest,
  ProblemDetails,
  UpdatedPersonalInformationResponse
} from "@app/services/bavel-payment-manager-client.service";
import { catchError, finalize, of, take, tap } from "rxjs";
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationsService } from "@shared/services/notifications.service";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: 'account-complete-profile',
  templateUrl: './account-complete-profile.component.html',
  styleUrls: ['./account-complete-profile.component.scss'],
  standalone: false
})
export class AccountCompleteProfileComponent implements OnInit {
  private formBuilder: FormBuilder = inject(FormBuilder);
  private accountClient: AccountClient = inject(AccountClient);
  private notificationsService: NotificationsService = inject(NotificationsService);
  private translateService: TranslateService = inject(TranslateService);
  public readonly specialCharactersDescription = '!@#$%^&*-_';

  public showSpinner: boolean = true;
  public visible: boolean = false;
  public personalInformationForm: FormGroup = this.formBuilder.group({
    firstName: ['', [Validators.required]],
    firstSurname: ['', [Validators.required]],
    secondSurname: [''],
    termsAccepted: [true, [Validators.requiredTrue]]
  });
  public hasPersonalInformation?: HasPersonalInformation;

  public ngOnInit(): void {
    this.accountClient.hasPersonalInformationCompleted().pipe(
      take(1),
      tap(hasPersonalInformation => {
        this.visible = !hasPersonalInformation.completed
        this.showSpinner = false;
        this.hasPersonalInformation = hasPersonalInformation;

        this.personalInformationForm.patchValue({
          firstName: this.isFullNameInvalid ? '' : hasPersonalInformation.firstName?.value,
          firstSurname: this.isFullNameInvalid ? '' : hasPersonalInformation.firstSurname?.value,
          secondSurname: this.isFullNameInvalid ? '' : hasPersonalInformation.secondSurname?.value,
          termsAccepted: hasPersonalInformation.termsAccepted
        });
      })
    ).subscribe();
  }

  public get isPersonalInformationInvalid(): boolean {
    return this.personalInformationForm.getError("invalid_personal_information");
  }

  public onHide(): void {
    this.visible = false;
  }

  public onConfirm(): void {
    this.personalInformationForm?.markAllAsTouched();
    if (this.isValidCompleteProfileComponent) {
      this.showSpinner = true;
      const personalInformation: PersonalInformationRequest = this.personalInformationForm.value;

      this.accountClient.completePersonalInformation(personalInformation)
        .pipe(
          tap(() => {
            this.visible = false;
          }),
          catchError((e: ProblemDetails | UpdatedPersonalInformationResponse) => {
            if (e instanceof ProblemDetails) {
              if (e.status === 500) {
                this.notificationsService.error(this.translateService.instant('complete_profile_error_internal_server_error'), 1500);
              }
            } else if (e instanceof UpdatedPersonalInformationResponse) {
              if (!e.isPersonalInformationValid) {
                this.notificationsService.warning(this.translateService.instant('complete_profile_error_personal_information_invalid'), 1500);
                this.personalInformationForm.setErrors({ ...this.personalInformationForm.errors, invalid_personal_information: true })
              }
            }
            return of(null);
          }),
          finalize(() => {
            this.showSpinner = false;
          }),
          take(1)
        )
        .subscribe();
    }
  }

  public get isValidCompleteProfileComponent() {
    return this.personalInformationForm.get('firstName')?.valid &&
      this.personalInformationForm.get('firstSurname')?.valid &&
      this.personalInformationForm.get('secondSurname')?.valid &&
      this.personalInformationForm.get('termsAccepted')?.valid;
  }

  public get isFullNameInvalid(): boolean {
    return !this.hasPersonalInformation?.firstName?.isValid ||
      !this.hasPersonalInformation?.firstSurname?.isValid ||
      !this.hasPersonalInformation?.secondSurname?.isValid;
  }
}
